import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { affiliateService } from '../../services/affiliateService';
import { useAffiliateSessionStore } from '../../store/affiliateSessionStore';

const AffiliateLogin: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { affiliateUserId, setSession, clearSession } = useAffiliateSessionStore();
  
  // Check if already logged in
  useEffect(() => {
    const id = affiliateUserId;
    if (id) {
      verifySession(id);
    }
  }, [affiliateUserId]);
  
  // Verify existing session
  const verifySession = async (affiliateUserId: string) => {
    try {
      const res = await affiliateService.verifySession(affiliateUserId);
      if (res && res.valid) {
        navigate('/affiliate/dashboard');
      } else {
        clearSession();
      }
    } catch (error) {
      console.error('Error verifying session:', error);
      clearSession();
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const data = await affiliateService.login(email, password);
      setSession(data.affiliateUserId, data.user);
      navigate('/affiliate/dashboard');
    } catch (err: any) {
      setError(err?.message || 'An error occurred during login');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <header className="bg-blue-600 text-white py-4 px-6">
        <h1 className="text-xl font-semibold">Explanatum Affiliate Portal</h1>
      </header>
      
      <main className="flex-grow flex flex-col justify-center py-8 px-4">
        <div className="max-w-md mx-auto w-full bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-2xl font-bold text-center mb-2">Affiliate Login</h1>
          
          <p className="text-gray-600 text-center mb-6">
            Access your affiliate dashboard to track commissions and manage your account
          </p>
          
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4" role="alert">
              <span className="block sm:inline">{error}</span>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
              <input
                id="email"
                name="email"
                type="email"
                required
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                id="password"
                name="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? (
                <span className="flex justify-center">
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </span>
              ) : 'Sign In'}
            </button>
          </form>
          
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Don't have an account? Contact the program owner to register as an affiliate.
            </p>
            
            <div className="mt-4">
              <Link to="https://explanatum.co.uk" className="text-sm text-blue-600 hover:text-blue-800">
                Return to Explanatum Homepage
              </Link>
            </div>
          </div>
        </div>
        
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            &copy; {new Date().getFullYear()} Explanatum. All rights reserved.
          </p>
        </div>
      </main>
    </div>
  );
};

export default AffiliateLogin;
